/* eslint-disable jsx-a11y/anchor-is-valid */
import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  TableContainer,
  Link,
  Tooltip,
} from '@mui/material';
import moment from 'moment';
import { useTheme } from '@mui/material/styles';
import { DownloadOutlined } from '@mui/icons-material';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { transformTransactionToInvoice } from 'renderer/features/payments-page/schemas/transaction.transformer';
import { TransactionDetails } from 'renderer/features/payments-page/schemas/transaction.schemas';
import { formatCurrency } from 'renderer/shared/formatters/formatters';
import ExportPDFView from './export-pdf';

const tableStyles = {
  tableContainer: {
    // marginTop: '20px',
    marginBottom: '20px',
    border: '1px solid rgba(224, 224, 224, 1)',
  },
  table: {
    minWidth: 650,
  },
  tableHead: {
    backgroundColor: '#f5f5f5',
  },
  tableCell: {
    borderBottom: '1px solid rgba(224, 224, 224, 1)',
  },
  tableRow: {
    '&:last-child td': {
      border: 0,
    },
  },
};

const PDFIconButton = ({
  transaction,
}: {
  transaction: TransactionDetails;
}) => {
  const theme = useTheme();
  const transactionInfo = transformTransactionToInvoice(transaction);
  return (
    <PDFDownloadLink
      document={<ExportPDFView transaction={transactionInfo} />}
      fileName={`${transaction._id}.pdf`}
    >
      <Tooltip title="Download" placement="top">
        <IconButton>
          <DownloadOutlined style={{ color: theme.palette.grey[900] }} />
        </IconButton>
      </Tooltip>
    </PDFDownloadLink>
  );
};

interface PaymentsProps {
  payments: TransactionDetails[];
  handleOpenModal: (serials: string[]) => void;
}

const Payments: React.FC<PaymentsProps> = ({ payments, handleOpenModal }) => {
  return (
    <TableContainer component={Paper} sx={tableStyles.tableContainer}>
      <Table sx={tableStyles.table} aria-label="licenses table" size="small">
        <TableHead sx={tableStyles.tableHead}>
          <TableRow>
            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
              PRODUCT
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
              CATEGORY
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
              DATE
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
              UNIT PRICE
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
              QUANTITY
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
              TOTAL
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
              PDF
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
              LICENSE KEY
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {payments.map((payment) => (
            <TableRow
              key={payment._id}
              sx={{
                '&:last-child td': {
                  border: 0,
                },
              }}
            >
              <TableCell align="center">{payment.productName}</TableCell>
              <TableCell align="center">{payment.category}</TableCell>
              <TableCell align="center">
                {' '}
                {moment(payment.createdAt).format('MM-DD-YYYY')}
              </TableCell>
              <TableCell align="center">${payment.fullPrice}</TableCell>
              <TableCell align="center">{payment.quantity}</TableCell>
              <TableCell align="center">
                {formatCurrency(payment.amount)}
              </TableCell>
              <TableCell align="center">
                <PDFIconButton transaction={{ ...payment }} />
              </TableCell>
              <TableCell>
                {payment.serials[0]}{' '}
                {(payment.quantity ?? 0) > 1 && (
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => handleOpenModal(payment.serials)}
                    sx={{
                      textDecoration: 'underline',
                      color: 'primary.main',
                      cursor: 'pointer',
                    }}
                  >
                    +{(payment.quantity ?? 2) - 1}
                  </Link>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default Payments;
